import { useSelector } from 'react-redux';
import AdditionalInfoFormContent from './AdditionalInfoFormContent/AdditionalInfoFormContent.tsx';
import { useAppDispatch } from '~/store/appDispatch.ts';
import { TPepEntity } from './types';
import { updateForm } from '~/store/identification-form/thunks/thunks.ts';
import { InformationMessageBlock } from '~/modules/identification/components/InformationMessage/InformationMessageBlock.tsx';
import { infoMessages } from '~/constants/infoMessages.ts';
import { Box, Button, Typography } from '@mui/material';
import { styles as main } from './styles.ts';
import { styles as root } from '~/modules/identification/styles.ts';
import { useAdditionalInformationForm } from '~/modules/identification/forms/AdditionalInformation/hooks/useAdditionalInformationForm.ts';
import { FormProvider, useFieldArray } from 'react-hook-form';
import { updateFormData, updateFormDataBack } from '~/store/identification-form/slices.ts';
import {
  IdentificationFormStepNames,
  IdentificationFormSteps,
} from '~/modules/identification/types.ts';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader.tsx';
import { OtherTaxResident } from '~/modules/identification/forms/AdditionalInformation/TaxResident/OtherTaxResident.tsx';
import { TIdentificationForm } from '~/store/identification-form/types.ts';
import { useFeatureFlags } from '~/helpers/FeatureFlagsContext.tsx';
import {
  testDataAdditionalInformationLegalResident,
  testDataAdditionalInformationSoleTrader,
} from '~/modules/identification/forms/AdditionalInformation/constants.ts';
import { ClientTypes } from '~/constants/clientTypes.ts';
import {
  selectApplicationId,
  selectClientType,
  selectIsReidentification,
} from '~/store/identification-form/selectors.ts';
import { onInvalidForm } from '~/helpers/utils.ts';
import { SetDirtyModalValues } from '../../components/SetDirtyModalValues.tsx';

interface AdditionalInformationProps {
  onNextStep: () => void;
}

export default function AdditionalInformation({ onNextStep }: AdditionalInformationProps) {
  const dispatch = useAppDispatch();
  const appId = useSelector(selectApplicationId);
  const isReidentification = useSelector(selectIsReidentification);
  const typeClient = useSelector(selectClientType);
  const { isTestEnvironmentEnabled } = useFeatureFlags();

  const form = useAdditionalInformationForm();
  const { isSubmitting, isDirty } = form.formState;

  const handleFillTestData = () =>
    form.reset(
      typeClient === ClientTypes.SOLE_TRADER
        ? testDataAdditionalInformationSoleTrader
        : testDataAdditionalInformationLegalResident
    );

  const { fields } = useFieldArray({
    control: form.control,
    name: 'additionalInformation',
  });

  const onSubmit =
    (isDirty: boolean, disableGoNext?: boolean) =>
    async (values: { additionalInformation: TPepEntity[] }) => {
      if (isDirty) {
        try {
          const info = values.additionalInformation as TPepEntity[];

          const additionalInformation = info.map((item) => {
            const { firstName, lastName, middleName, ...rest } = item;
            return {
              ...rest,
              accounts: rest.hasForeignTaxResidency ? rest.accounts : null,
            };
          });
          const data = {
            additionalInformation: {
              additionalInformation,
            },
            applicationId: appId as number,
          };
          await dispatch(
            updateForm({ data, reportId: IdentificationFormSteps.additionalInformation })
          );
          const formData = {
            field: IdentificationFormStepNames.additionalInformation as keyof TIdentificationForm,
            data: { additionalInformation },
          };
          dispatch(updateFormData(formData));
          dispatch(updateFormDataBack(formData));
          !disableGoNext && onNextStep();
        } catch (error) {
          console.log(error);
        }
      } else {
        !disableGoNext && onNextStep();
      }
    };

  return (
    <FormProvider {...form}>
      <Box component="form" onSubmit={form.handleSubmit(onSubmit(isDirty), onInvalidForm)}>
        {isTestEnvironmentEnabled && (
          <Box sx={root.testEnvironmentButton}>
            <Button variant="outlined" onClick={handleFillTestData}>
              Заповнити даними
            </Button>
          </Box>
        )}
        {isReidentification && (
          <InformationMessageBlock informationText={infoMessages.RE_VERIFICATION_MESSAGE} />
        )}
        <Typography sx={main.titleInfo} variant="h4">
          Інформація про належність Клієнта до РЕР
        </Typography>
        {fields?.map((_, index) => (
          <AdditionalInfoFormContent key={index} index={index} />
        ))}
        {fields.map((field, index) => (
          <OtherTaxResident key={field.id} index={index} />
        ))}
        <Box sx={main.buttonBox}>
          <Button disabled={isSubmitting} type="submit" variant="contained">
            Продовжити
          </Button>
        </Box>
        <SetDirtyModalValues
          saveValues={form.handleSubmit(onSubmit(isDirty, true), onInvalidForm)}
        />
      </Box>
      {isSubmitting && <FullPageLoader />}
    </FormProvider>
  );
}
